/**
 * Funções do `FunctionFactory` do Survey.
 */
var pad = function (value, size) {
    if (size === void 0) { size = 2; }
    return "".concat(value).padStart(size, "0");
};
export var formatDateISO = function (date) {
    return "".concat(date.getFullYear(), "-").concat(pad(date.getMonth() + 1), "-").concat(pad(date.getDate()));
};
export var formatDateTimeISO = function (date) {
    return "".concat(formatDateISO(date), "T").concat(pad(date.getHours()), ":").concat(pad(date.getMinutes()), ":").concat(pad(date.getSeconds()));
};
export var toDate = function (value) {
    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === "number") {
        var fromNumber = new Date(value);
        return isNaN(fromNumber.getTime()) ? null : fromNumber;
    }
    if (typeof value !== "string" || !value.trim()) {
        return null;
    }
    var text = value.trim();
    // "2026-08-31" sem hora seria lido como UTC; forçar meia-noite local evita
    // a data "andar" um dia em fusos negativos.
    var dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
    if (dateOnly) {
        var year = dateOnly[1], month = dateOnly[2], day = dateOnly[3];
        return new Date(Number(year), Number(month) - 1, Number(day));
    }
    var parsed = new Date(text);
    return isNaN(parsed.getTime()) ? null : parsed;
};
var startOfToday = function (offsetInDays) {
    var date = new Date();
    date.setHours(0, 0, 0, 0);
    if (typeof offsetInDays === "number" && !isNaN(offsetInDays)) {
        date.setDate(date.getDate() + offsetInDays);
    }
    return date;
};
var isEmptyValue = function (value) {
    return value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0);
};
var toNumberOrUndefined = function (value) {
    if (typeof value === "number") {
        return isNaN(value) ? undefined : value;
    }
    if (typeof value === "string" && value.trim() !== "") {
        var parsed = Number(value.replace(",", "."));
        return isNaN(parsed) ? undefined : parsed;
    }
    return undefined;
};
var flattenParams = function (values) {
    var result = [];
    var visit = function (value) {
        if (value === undefined || value === null) {
            return;
        }
        if (Array.isArray(value)) {
            value.forEach(visit);
            return;
        }
        var asNumber = toNumberOrUndefined(value);
        result.push(asNumber === undefined ? value : asNumber);
    };
    values.forEach(visit);
    return result;
};
var sumOf = function (values) {
    return flattenParams(values).reduce(function (total, value) { var _a; return total + ((_a = toNumberOrUndefined(value)) !== null && _a !== void 0 ? _a : 0); }, 0);
};
var minMaxOf = function (values, isMin) {
    var flat = flattenParams(values);
    var result = undefined;
    flat.forEach(function (value) {
        if (result === undefined) {
            result = value;
            return;
        }
        if (isMin
            ? result > value
            : result < value) {
            result = value;
        }
    });
    return result;
};
var diffInMonths = function (from, to) {
    var months = (to.getFullYear() - from.getFullYear()) * 12 +
        (to.getMonth() - from.getMonth());
    return to.getDate() < from.getDate() ? months - 1 : months;
};
var diffDaysBetween = function (from, to) {
    return Math.ceil(Math.abs(to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
};
var dateDiffBy = function (from, to, unit) {
    if (unit === "days") {
        return diffDaysBetween(from, to);
    }
    var months = diffInMonths(from, to);
    return unit === "months" ? months : Math.trunc(months / 12);
};
var dateFromArgOrToday = function (args) {
    return args.length > 0 && !isEmptyValue(args[0]) ? toDate(args[0]) : startOfToday();
};
var asString = function (value) {
    return typeof value === "string" ? value : "";
};
/** Campo de matriz/painel dinâmico chega como array de linhas. */
var toRows = function (value) {
    if (Array.isArray(value)) {
        return value.filter(function (item) {
            return !!item && typeof item === "object";
        });
    }
    if (value && typeof value === "object") {
        return Object.values(value).filter(function (item) {
            return !!item && typeof item === "object";
        });
    }
    return [];
};
export var createExpressionFunctions = function (_a) {
    var evaluateCondition = _a.evaluateCondition;
    var collectInArray = function (args) {
        if (args.length < 2 || args.length > 3) {
            return undefined;
        }
        var rows = toRows(args[0]);
        var column = args[1];
        if (typeof column !== "string") {
            return undefined;
        }
        var filter = typeof args[2] === "string" ? args[2] : undefined;
        var collected = [];
        rows.forEach(function (row) {
            var _a;
            if (isEmptyValue(row[column])) {
                return;
            }
            if (filter && !evaluateCondition(filter, row)) {
                return;
            }
            collected.push((_a = toNumberOrUndefined(row[column])) !== null && _a !== void 0 ? _a : NaN);
        });
        return collected;
    };
    var countIn = function (args) { var _a; return ((_a = collectInArray(args)) !== null && _a !== void 0 ? _a : []).length; };
    var sumIn = function (args) {
        var _a;
        return ((_a = collectInArray(args)) !== null && _a !== void 0 ? _a : []).reduce(function (total, value) { return total + (isNaN(value) ? 0 : value); }, 0);
    };
    return {
        // --- agregação sobre os parâmetros ---
        sum: function (args) { return sumOf(args); },
        min: function (args) { return minMaxOf(args, true); },
        max: function (args) { return minMaxOf(args, false); },
        count: function (args) { return flattenParams(args).length; },
        avg: function (args) {
            var flat = flattenParams(args);
            return flat.length > 0 ? sumOf(args) / flat.length : 0;
        },
        // --- agregação sobre linhas de matriz / painel dinâmico ---
        sumInArray: function (args) { return sumIn(args); },
        countInArray: function (args) { return countIn(args); },
        avgInArray: function (args) {
            var total = countIn(args);
            return total === 0 ? 0 : sumIn(args) / total;
        },
        minInArray: function (args) {
            var _a;
            var values = ((_a = collectInArray(args)) !== null && _a !== void 0 ? _a : []).filter(function (v) { return !isNaN(v); });
            return values.length ? Math.min.apply(Math, values) : undefined;
        },
        maxInArray: function (args) {
            var _a;
            var values = ((_a = collectInArray(args)) !== null && _a !== void 0 ? _a : []).filter(function (v) { return !isNaN(v); });
            return values.length ? Math.max.apply(Math, values) : undefined;
        },
        // --- lógica ---
        iif: function (args) { return (args.length !== 3 ? "" : args[0] ? args[1] : args[2]); },
        // --- datas ---
        today: function (args) {
            return formatDateISO(startOfToday(args.length ? toNumberOrUndefined(args[0]) : undefined));
        },
        currentDate: function () { return formatDateTimeISO(new Date()); },
        getDate: function (args) {
            var date = args.length ? toDate(args[0]) : null;
            return date ? formatDateISO(date) : null;
        },
        currentYear: function () { return new Date().getFullYear(); },
        getYear: function (args) {
            var date = args.length ? toDate(args[0]) : null;
            return date ? date.getFullYear() : undefined;
        },
        year: function (args) { var _a; return (_a = dateFromArgOrToday(args)) === null || _a === void 0 ? void 0 : _a.getFullYear(); },
        month: function (args) {
            var date = dateFromArgOrToday(args);
            return date ? date.getMonth() + 1 : undefined;
        },
        day: function (args) { var _a; return (_a = dateFromArgOrToday(args)) === null || _a === void 0 ? void 0 : _a.getDate(); },
        weekday: function (args) { var _a; return (_a = dateFromArgOrToday(args)) === null || _a === void 0 ? void 0 : _a.getDay(); },
        age: function (args) {
            var born = args.length ? toDate(args[0]) : null;
            if (!born)
                return null;
            var unit = (args.length > 1 ? asString(args[1]) : "") || "years";
            return dateDiffBy(born, startOfToday(), unit);
        },
        dateDiff: function (args) {
            if (args.length < 2)
                return null;
            var from = toDate(args[0]);
            var to = toDate(args[1]);
            if (!from || !to)
                return null;
            var unit = (args.length > 2 ? asString(args[2]) : "") || "days";
            return dateDiffBy(from, to, unit);
        },
        diffDays: function (args) {
            if (args.length !== 2)
                return 0;
            var from = toDate(args[0]);
            var to = toDate(args[1]);
            return from && to ? diffDaysBetween(from, to) : 0;
        },
        // --- texto ---
        substring: function (args) {
            if (args.length < 2)
                return "";
            var text = args[0];
            if (typeof text !== "string")
                return "";
            var start = toNumberOrUndefined(args[1]);
            if (start === undefined)
                return "";
            var end = args.length > 2 ? toNumberOrUndefined(args[2]) : undefined;
            return end === undefined
                ? text.substring(start)
                : text.substring(start, end);
        },
    };
};
